import React, { Component } from 'react';
import { isEqual, cloneDeep, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { DropTarget as dropTarget } from 'react-dnd';
import axios from 'axios';
import ContentEditable from 'react-contenteditable';
import DnDItemTypes from './DynamicDnD/DnDItemTypes';
import Modal from './Modal/Modal';
import { ROOT_URL, MRI_FUNCTION_STUDIES } from '../../actions/urlTypes';
import { FLEX_STUDY_TAG } from '../../constants';
import { GEOGRAPHY_BASES } from '../../actions/actionTypes';

const modalStyle = {
  width: '400px',
  height: 'auto',
  left: '35%',
  top: '30%',
};

const ValidOperators = [
  '(',
  ')',
  '>=',
  '<=',
  '<',
  '>',
  '=',
  '+',
  '-',
  '*',
  '/',
  ',',
  'AND',
  'OR',
  'NOT',
  'XOR',
];
/** this is a list of all the functions the queryeditor will accept.
 * The syntax string is a regular expression that must exactly match the
 * argumentSyntax attribute created by the parser.
 * B=boolean keyword, N=numeric keyword, d=numeric litteral
 * As the parser goes deeper into function nesting, it keeps adding things onto a stack
 * Upon completing a correctly formatted function call, it will pop that function off
 * the stack and add its evaluatesTo attribute to the argumentSyntax attribute
 * of the next member up.
 */

// regexes
// const syntaxError = /^o|oo|ii|o\)|\(o|i\(|\)i|n\)|\)\(|no|n$|o$|[(on],|,[()o]/; 
const functionSyntaxError = /f[^\(]/;
const whitespace = /\s+/;
const numericLiteral = /^(\d+)?(\.)?(\d+)?$/;
// created new regex for 18052.. 
const syntaxError = /^o|oo|ii|o\)|\(o|i\(|\)i|n\)|\)\(|no|n$|o$|[(on],/;

const functionList = [
  { name: 'COUNTRANGE', syntax: 'B+dd', evaluatesTo: 'N' },
  { name: 'RANGE', syntax: 'B+dd', evaluatesTo: 'B' }, // added as part of PLAT-15152
  { name: 'MEAN', syntax: 'N', evaluatesTo: 'N' },
  { name: 'MEANNOZERO', syntax: 'N', evaluatesTo: 'N' },
  { name: 'MEANSCORE', syntax: '([NB]d)+', evaluatesTo: 'N' },
  { name: 'MEANSCORENOZERO', syntax: '([NB]d)+', evaluatesTo: 'N' },
  { name: 'VOLUME', syntax: '([B]d)+', evaluatesTo: 'N' },
  { name: 'NTILE', syntax: 'Ndd', evaluatesTo: 'N' },
  { name: 'INCOUNTY', syntax: 'd+', evaluatesTo: 'B' },
  { name: 'INMARKET', syntax: 'd+', evaluatesTo: 'B' },
  { name: 'INSTATE', syntax: 'd+', evaluatesTo: 'B' },
  { name: 'INZIP', syntax: 'd+', evaluatesTo: 'B' },
  { name: 'INREGION', syntax: 'd+', evaluatesTo: 'B' },
  { name: 'MEANNONULL', syntax: '([NB]d)+', evaluatesTo: 'N' },
  { name: 'MEANSCORENONULL', syntax: '([NB]d)+', evaluatesTo: 'N' },
  { name: 'MEDIAN', syntax: 'N', evaluatesTo: 'N' },
  { name: 'MEDIANNOZERO', syntax: 'N', evaluatesTo: 'N' },
  { name: 'MEDIANDISCRETE', syntax: 'N', evaluatesTo: 'N' },
  { name: 'MEDIANDISCRETENOZERO', syntax: 'N', evaluatesTo: 'N' },
  { name: 'NTILEWT', syntax: 'Ndd', evaluatesTo: 'N' },
  { name: 'REACH', syntax: '([N]d)+', evaluatesTo: 'N' },
  { name: 'XP', syntax: '([N]d)+', evaluatesTo: 'N' },
  { name: 'IN', syntax: 'Nd+', evaluatesTo: 'B' },
];

/** Temporary code added for PLAT-7946, in non-production environment */

const MRIFunctionList = [
  { name: 'AVG', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDSCREEN', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDINHOME', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDOUTOFHOME', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDPRIMARY', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDSECONDARY', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDBUYERS', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDDOCTOR', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDBARBER', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDLIBRARY', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDRECEPTION', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDCOMMUTE', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDPLANE', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDTRAVEL', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDATWORK', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDSTORE', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDOTHERHOME', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDOWNHOME', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDELSEWHERE', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDANYACT', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDRTFAV', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDRTVERY', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDRTGOOD', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDRTFAIR', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDRTPOOR', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDWEBSITE', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDADNOTED', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDDIGPR', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUDPDSCREEN', syntax: 'N', evaluatesTo: 'N' },
  { name: 'AUD', syntax: 'N', evaluatesTo: 'N' },
  { name: 'NETANY', syntax: 'N', evaluatesTo: 'N' },
];
/** Temporarry code block ended */

const keyWordTarget = {
  drop: (props, monitor, component) => {
    const objSmartSearchArray = {};
    const breadcrumbList = JSON.parse(window.localStorage.getItem('breadCrumb'));
    const breadCrumbValue = breadcrumbList ? breadcrumbList.reverse().join(' > ') : undefined;
    if (monitor.getItemType() === DnDItemTypes.DnD_ITEM_TYPE_2) {
      const selectedNodes = cloneDeep(monitor.getItem().selectedNodes);
      const objectsAdded = [];
      const expressionFormed = [];
      const tags = [];

      selectedNodes.forEach(node => {
        node.breadcrumbs = breadCrumbValue;
        objectsAdded.push(node);
        expressionFormed.push(node.keyword);
        tags.push(node.shortText);
      });
      objSmartSearchArray.objectsAdded = objectsAdded;
      objSmartSearchArray.expressionFormed = expressionFormed;
      objSmartSearchArray.tags = tags;
      component.handleKeywordsDrop(
        monitor.getItem().selectedNodes.map(node => ({
          label: node.keyword.toUpperCase(),
          tooltip: node.shortText,
          recordType: node.rectype,
          keyword: node.keyword,
        })),
        objSmartSearchArray,
      );
    } else if (monitor.getItemType() === DnDItemTypes.DnD_ITEM_TYPE_3) {
      const selectedEntries = cloneDeep(monitor.getItem().selectedEntries);
      if (selectedEntries[0].userDefinedText) {
        component.handleUserDefinedText(selectedEntries[0].title);
      }
      selectedEntries.forEach(node => {
        if (node.dataSmartSearch && Object.keys(node.dataSmartSearch).length > 0) {
          // node.dataSmartSearch.objectsAdded.breadcrumbs = breadCrumbValue;
          const objectsAdded = node.dataSmartSearch.objectsAdded;
          objectsAdded.forEach(node => {
            if (!objectsAdded.shortTextCatgView) {
              node.shortTextCatgView = node.name || node.title || node.shortText;
            }
          });
          objSmartSearchArray.objectsAdded = objectsAdded;
          objSmartSearchArray.expressionFormed = node.dataSmartSearch.expressionFormed;
          objSmartSearchArray.tags = node.dataSmartSearch.tags;
        } else {
          objSmartSearchArray.objectsAdded = [];
          objSmartSearchArray.expressionFormed = [];
          objSmartSearchArray.tags = [];
        }
      });

      component.handleKeywordsDrop(
        monitor.getItem().selectedEntries.map(entry => ({
          label: entry.keyword.toUpperCase(),
          tooltip: entry.title,
          keyword: entry.keyword,
          recordType: entry.rectype,
          isLabelUpdated: entry.isLabelUpdated || false,
        })),
        objSmartSearchArray
      );
    } else if (monitor.getItemType() === DnDItemTypes.DnD_ITEM_TYPE_4) {
      const selectedNodes = cloneDeep(monitor.getItem().selectedNodes);

      const objectsAdded = [];
      const expressionFormed = [];
      const tags = [];
      selectedNodes.forEach(node => {
        node.breadcrumbs = breadCrumbValue;
        node.keyword = node.expression;
        node.shortTextCatgView = node.title;
        objectsAdded.push(node);
        expressionFormed.push(node.expression);
        tags.push(node.title);
      });
      const newObjsAdded = objectsAdded;
      objSmartSearchArray.objectsAdded = newObjsAdded;
      objSmartSearchArray.expressionFormed = expressionFormed;
      objSmartSearchArray.tags = tags;
      component.handleKeywordsDrop(
        selectedNodes.map(node => ({
          label: node.expression,
          tooltip: node.title,
          isLabelUpdated: false,
          recordType: 'D',
          keyword: node.keyword,
        })),
        objSmartSearchArray,
      );
    } else if (monitor.getItemType() === DnDItemTypes.DnD_ITEM_TYPE_7) {
      const selectedNodes = cloneDeep(monitor.getItem().selectedNodes);
      const objectsAdded = [];
      const expressionFormed = [];
      const tags = [];
      for (const key in selectedNodes) {
        selectedNodes[key].forEach(geos => {
          const geoBreadCrumb = `Geographies > ${geos.fname.substring(2, geos.fname.length)}`;
          geos.breadcrumbs = geoBreadCrumb;
          geos.shortTextCatgView = geos.name.toUpperCase();
          objectsAdded.push(geos);
          expressionFormed.push(geos.fname);
          tags.push(geos.name);
        });
      }
      objSmartSearchArray.objectsAdded = objectsAdded;
      objSmartSearchArray.expressionFormed = expressionFormed;
      objSmartSearchArray.tags = tags;
      component.handleKeywordsDrop(monitor.getItem().selectedNodes, objSmartSearchArray);
    }
  },
};

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
    canDrop: monitor.canDrop(),
  };
}

class ExpressionCodingBox extends Component {
  constructor(props) {
    super(props);
    this.state = {
      html: '',
      droppable: false,
      identifiers: {},
      functions: {},
      autoFormat: false,
      syntaxExpression: '',
      isExpValid: false,
      isVerifiable: false,
      expBoxTitle: '',
      anchorOffset: 0,
      objArray: {},
      functionExpList: [],
      objSmartSearchArray: {},
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.boxTitle !== nextProps.boxTitle) {
      this.setState({ expBoxTitle: nextProps.boxTitle });
    }
  }

  getExpressionState = () => {
    this.validateFormatContent(false);
    return this.state;
  };

  handleVerify = loginData => {
    const ebIdentifiers = this.state.identifiers;
    const identifiersFormed = [];
    const inValidIdentifiers = [];
    for (const key in ebIdentifiers) {
      if (!(ebIdentifiers[key].isLiteral === true)) {
        if (!ebIdentifiers[key].isValidated) {
          inValidIdentifiers.push(key);
        }
      }
    }
    const url = `${ROOT_URL}/dictionary/validate`;
    if (inValidIdentifiers.length && loginData.productNo) {
      axios({
        url,
        method: 'post',
        headers: { Authorization: `Bearer ${loginData.token}` },
        data: {
          uid: loginData.uid,
          accountId: loginData.activeAccountId,
          productNo: loginData.productNo,
          keywords: inValidIdentifiers,
        },
        withCredentials: true,
        responseType: 'json',
        validateStatus: status =>
          (status >= 400 && status < 423) || status === 200,
      })
        .then(response => {
          if (response.status === 200) {
            const responseVerified = response.data.data;
            for (const key in responseVerified) {
              let isServerValidatedResult = true;
              if (responseVerified[key].text === null) {
                isServerValidatedResult = 'e';
              }
              identifiersFormed.push({
                label: responseVerified[key].keyword,
                tooltip: responseVerified[key].text,
                recordType: responseVerified[key].type,
                isServerValidatedResult,
              });
            }
            this.handleVerifiedKeywords(identifiersFormed);
          } else if (response.status === 422) {
            inValidIdentifiers.map(key => {
              identifiersFormed.push({
                label: key,
                tooltip: '',
                recordType: null,
                isServerValidatedResult: 'e',
              });
              return true;
            });
            this.handleVerifiedKeywords(identifiersFormed);
          }
        })
        .catch(response => console.warn(response));
    } else {
      this.validateFormatContent(true);
    }
  };

  handleUserDefinedText = title => {
    this.props.handleUserDefinedText(title);
  }

  handleSelectBoxOperator = selectedOperator => {
    const keywords = this.state.dropKeywords;
    let expBoxTitle = this.state.expBoxTitle;
    if (selectedOperator === 'AND' || selectedOperator === 'OR') {
      if (keywords && keywords[0]) {
        expBoxTitle = `${expBoxTitle} ( ${keywords[0].tooltip}`;
      } else {
        expBoxTitle = `${expBoxTitle} (`;
      }
      const inputs = keywords.slice(1).reduce(
        (acc, val) => {
          acc.push(selectedOperator);
          acc.push(val.label);
          expBoxTitle = `${expBoxTitle} ${selectedOperator} ${val.tooltip}`;
          return acc;
        },
        ['(', keywords[0].label],
      );
      inputs.push(')');
      expBoxTitle = `${expBoxTitle} )`;
      const eboxElem = document.getElementById('expression-coding-box');
      eboxElem.focus();
      this.pasteHtmlAtCaret(eboxElem, inputs.join(' '));
      this.setState({ expBoxTitle });
    }
    this.multiKeywordDropModal.hide();
  };

  isValidFunction = name => {
    let finalFunctionList = [];
    if (this.props.moduleStudy && this.props.moduleStudy.ss === 'MRI') {
      finalFunctionList =  [...MRIFunctionList, ...functionList];
    } else {
      finalFunctionList = functionList;
    }
    return finalFunctionList.find(element => element.name === name);
  }

  handleAutoformat = boolAutoFormat => {
    this.setState({ autoFormat: boolAutoFormat });
    let txtHtml = '';
    const formattedHTML = document.createElement('div');
    const tabs = '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;';
    const space = '&nbsp;&nbsp;&nbsp;&nbsp;';
    formattedHTML.innerHTML = this.state.html;
    if (boolAutoFormat) {
      const spans = formattedHTML.getElementsByTagName('span');
      for (let i = 0; i < spans.length; i += 1) {
        const wrapper = document.createElement('div');
        spans[i].parentNode.insertBefore(wrapper, spans[i]);
        wrapper.appendChild(spans[i]);
      }
      const divs = formattedHTML.getElementsByTagName('div');
      let col = 0;
      let tabRequired = 0;
      let fns = 0;
      const syntaxArr = this.state.syntaxExpression.split('');
      for (let j = 0; j < syntaxArr.length; j += 1) {
        tabRequired = fns * 1;
        if (syntaxArr[j] === '(') {
          txtHtml += `${divs[j].innerHTML}<br/>`;
          if (fns === 0) {
            tabRequired = 1;
          }
          col = 1;
        } else if (syntaxArr[j] === ')') {
          if (fns > 0) {
            fns -= 1;
          }
          tabRequired = fns * 1;
          if (tabRequired === 1) {
            txtHtml += `<br/>${tabs}${divs[j].innerHTML}`;
          } else if (tabRequired === 2) {
            txtHtml += `<br/>${tabs}${tabs}${divs[j].innerHTML}`;
          } else if (tabRequired === 3) {
            txtHtml += `<br/>${tabs}${tabs}${tabs}${divs[j].innerHTML}`;
          } else {
            txtHtml += `<br/>${divs[j].innerHTML}`;
          }
          col = 0;
        } else if (syntaxArr[j] === 'f') {
          fns += 1;
          if (tabRequired === 1) {
            txtHtml += `${tabs}${divs[j].innerHTML}`;
          } else if (tabRequired === 2) {
            txtHtml += `${tabs}${tabs}${divs[j].innerHTML}`;
          } else if (tabRequired === 3) {
            txtHtml += `${tabs}${tabs}${tabs}${divs[j].innerHTML}`;
          } else {
            txtHtml += divs[j].innerHTML;
          }
          col = 0;
        } else {
          if (tabRequired === 1 && col === 1) {
            txtHtml += `${tabs}${divs[j].innerHTML}`;
          } else if (tabRequired === 2 && col === 1) {
            txtHtml += `${tabs}${tabs}${divs[j].innerHTML}`;
          } else if (tabRequired === 3 && col === 1) {
            txtHtml += `${tabs}${tabs}${tabs}${divs[j].innerHTML}`;
          } else if (tabRequired === 4 && col === 1) {
            txtHtml += `${tabs}${tabs}${tabs}${tabs}${divs[j].innerHTML}`;
          } else {
            txtHtml += `${space}${divs[j].innerHTML}`;
          }
          col = 0;
        }
      }
      this.setState({ html: `${txtHtml}&nbsp;` }, () => {
        const el = document.getElementById('expression-coding-box');
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        if (el.lastChild) {
          range.setStart(el.lastChild, 1);
        }
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
        el.focus();
      });
    } else {
      txtHtml = this.state.html.replace(/(<br\s*[\/]?>|&nbsp;)+/gi, '&nbsp;');
      this.setState({ html: txtHtml }, () => {
        const el = document.getElementById('expression-coding-box');
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        if (el.lastChild) {
          range.setStart(el.lastChild, 1);
        }
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
        el.focus();
      });
    }
  };

  placeCaretAtEnd = el => {
    el.focus();
    if (
      typeof window.getSelection !== 'undefined' &&
      typeof document.createRange !== 'undefined'
    ) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } else if (typeof document.body.createTextRange !== 'undefined') {
      const textRange = document.body.createTextRange();
      textRange.moveToElementText(el);
      textRange.collapse(false);
      textRange.select();
    }
  };

  pasteHtmlAtCaret = (elem, html, editProbability) => {
    elem.focus();
    let sel;
    let range;
    if (window.getSelection) {
      // IE9 and non-IE
      sel = window.getSelection();
      if (sel.anchorOffset !== this.state.anchorOffset) {
        this.placeCaretAtEnd(elem);
      }
      if (sel.getRangeAt && sel.rangeCount) {
        range = sel.getRangeAt(0);
        range.deleteContents();

        // Range.createContextualFragment() would be useful here but is
        // non-standard and not supported in all browsers (IE9, for one)
        const el = document.createElement('div');
        el.innerText = ` ${html} `;
        const frag = document.createDocumentFragment();
        let node;
        let lastNode;
        while ((node = el.firstChild)) {
          lastNode = frag.appendChild(node);
        }
        range.insertNode(frag);
        // Preserve the selection
        if (lastNode) {
          range = range.cloneRange();
          range.setStartAfter(lastNode);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    } else if (document.selection && document.selection.type !== 'Control') {
      // IE < 9
      document.selection.createRange().pasteHTML(html);
    }
    const eboxElem = document.getElementById('expression-coding-box');

    this.setState(
      { html: eboxElem.innerHTML, anchorOffset: sel.anchorOffset },
      () => {
        this.validateFormatContent(false, undefined, undefined, undefined, editProbability);
      },
    );
  };

  addslash = txt => txt.replace(/</g, '&lt;');

  revertSlash = txt => txt.replace(/&lt;/gi, '<');

  /**
   * ValidateContent method is a temporary method added for PLAT-7946, in non-production environment
   * @param {bool} isServerRequired
   * @param {array} newInputs
   * @param {bool} isVerify
   * @param {obj} verifyData
   */
  validateContent = (
    isServerRequired,
    newInputs,
    isVerify,
    verifyData,
  ) => {
    const contentExtractor = document.createElement('div');
    contentExtractor.innerHTML = this.state.html;
    const extractedContents = (
      contentExtractor.textContent || contentExtractor.innerText
    )
    .replace(/\(/g, ' ( ')
      .replace(/\)/g, ' ) ')
      .replace(/,/g, ' , ')
      .split(whitespace);
    const extractedContentsWithoutSpaces = (
      contentExtractor.textContent || contentExtractor.innerText
    )
    .replace(/\(/g, ' ( ')
      .replace(/\)/g, ' ) ')
      .replace(/,/g, ' , ')
      .replace(/\s+/g, '');
    const extractedContentWithoutFunc = extractedContentsWithoutSpaces
      .substring(extractedContentsWithoutSpaces.indexOf('(') + 1, extractedContentsWithoutSpaces.length - 1)
      .split(',');
    let mriFunctionFlag = false;
    extractedContents.forEach(content => {
      if (MRIFunctionList.some(element => element.name === content)) {
        mriFunctionFlag = true;
      }
    });
    if (mriFunctionFlag) {
      // const keyword = extractedContents.join(' ');
      const url = `${ROOT_URL}/dictionary/validate`;
      axios({
        url,
        method: 'post',
        headers: { Authorization: `Bearer ${verifyData.token}` },
        data: {
          uid: verifyData.uid,
          accountId: verifyData.activeAccountId,
          productNo: verifyData.productNo,
          keywords: [...extractedContentWithoutFunc],
        },
        withCredentials: true,
        responseType: 'json',
        validateStatus: status =>
          (status >= 400 && status < 423) || status === 200,
      })
        .then(response => {
          const responseVerified = response.data.data;
          const identifiersFormed = [];
          for (const key in responseVerified) {
            let isServerValidatedResult = true;
            if (responseVerified[key].text === null) {
              isServerValidatedResult = 'e';
            }
            identifiersFormed.push({
              label: responseVerified[key].keyword,
              tooltip: responseVerified[key].text,
              recordType: responseVerified[key].type,
              isServerValidatedResult,
            });
          }
          this.handleVerifiedKeywords(identifiersFormed);
          this.setState({ html: `${responseVerified[keyword].text}` });
        })
        .catch(response => console.warn(response));
    } else {
      this.validateFormatContent(
        isServerRequired,
        newInputs,
        isVerify,
        verifyData,
      );
    }
  };

  validateFormatContent = (
    isServerRequired,
    newInputs,
    isVerify,
    verifyData,
    editProbability,
  ) => {
    const contentExtractor = document.createElement('div');
    contentExtractor.innerHTML = this.state.html;
    const extractedContents = (
      contentExtractor.textContent || contentExtractor.innerText
    )
      .replace(/\(/g, ' ( ')
      .replace(/\)/g, ' ) ')
      .replace(/,/g, ' , ')
      .split(whitespace);
    if (newInputs) {
      extractedContents.push(...newInputs);
    }
    const expElements = [];
    let syntaxStr = '';
    let isExpValid = false;
    const identifiers = this.state.identifiers;

    for (let i = 0; i < extractedContents.length; i += 1) {
      const element = this.revertSlash(extractedContents[i].toUpperCase());
      const operatorIndex = ValidOperators.indexOf(element);
      const functionObj = this.isValidFunction(element);
      let isLiteral = false;

      if (operatorIndex !== -1) {
        let operatorType = '';
        if (element === '(') {
          operatorType = '('; // group open
        } else if (element === ')') {
          operatorType = ')'; // group close
        } else if (element === ',') {
          operatorType = ',';
        } else if (element === 'NOT') {
          operatorType = 'n'; // negator
        } else {
          operatorType = 'o'; // any other operator
        }
        syntaxStr += operatorType;
        expElements.push({ label: element, type: operatorType });
      } else if (functionObj) {
        expElements.push({
          label: element.toUpperCase(),
          type: 'f',
          functionInfo: functionObj,
        }); // identifier (keyword)
        syntaxStr += 'f';
      } else if (element !== '') {
        // item is an identifier, (keyword or litteral)
        const identifier = this.state.identifiers[element];
        if (numericLiteral.test(element)) {
          isLiteral = true;
        }
        const breadcrumbList = JSON.parse(window.localStorage.getItem('breadCrumb'));
        const breadCrumbs = breadcrumbList ? breadcrumbList.reverse().join(' > ') : undefined;
        if (!identifier && !isLiteral) {
          identifiers[element] = {
            isValidated: false,
            tooltip: `${element}`,
            isLiteral,
            keyword: `${extractedContents[i]}`,
            breadCrumbs,
          };
          this.setState({
            identifiers: {
              ...identifiers,
            },
          });
          isExpValid = false;
        }
        if (identifier) {
          expElements.push({
            ...identifier,
            label: element,
            type: 'i',
            isLiteral,
            keyword: `${extractedContents[i]}`,
          }); // identifier (keyword)
        } else {
          expElements.push({
            ...identifiers[`${element} `],
            label: element,
            type: 'i',
            isLiteral,
            keyword: `${extractedContents[i]}`,
          }); // identifier (keyword)
        }
        syntaxStr += 'i';
      }
    }
    const parenStack = [];
    const functionStack = [];
    // const parenNestAtLastFunction = 0;
    isExpValid = true;
    for (let i = 0; i < expElements.length; i += 1) {
      if (expElements[i].type === '(') {
        parenStack.push(i);
      } else if (expElements[i].type === ')') {
        if (parenStack.length === 0) {
          expElements[i] = {
            label: ')',
            type: 'e',
            errorMsg: 'extra closing paren',
          };
          isExpValid = false;
          break;
        }
        const twin = parenStack.pop(); // not using this now, but it will probably be useful later.
        expElements[twin].twinIndex = i;
        expElements[i].twinIndex = twin;
        if (
          functionStack.length !== 0 &&
          parenStack.length ===
            functionStack[functionStack.length - 1].nestLevel
        ) {
        // this is the close of a function, record this position, j, as the end of the thisFunction
          const thisFunction = functionStack[functionStack.length - 1];
          expElements[thisFunction.index].closingParen = i;
        // before poping the function off the stack,
        // copy a ref to it's function info to the elements item
          expElements[i].functionInfo = thisFunction.functionInfo;
          functionStack.pop();
        }
      } else if (expElements[i].type === 'f') {
        functionStack.push({
          index: i,
          functionInfo: expElements[i].functionInfo,
          nestLevel: parenStack.length,
        });
      } else if (expElements[i].type === ',' && functionStack.length <= 0) {
        if (functionStack.length === 0) {
          // comma found outside any function
          expElements[i] = {
            label: ',',
            type: 'e',
            errorMsg: 'misplaced comma',
          };
          isExpValid = false;
          break;
        }
      }
    }

    if (isExpValid && parenStack.length !== 0) {
      expElements[parenStack.pop()] = {
        label: '(',
        type: 'e',
        errorMsg: 'unclosed paren',
      };
      isExpValid = false;
    }
    const syntaxErrorIndex = syntaxStr.search(syntaxError);
    if (isExpValid && syntaxErrorIndex !== -1) {
      expElements[syntaxErrorIndex].type = 'e';
      expElements[syntaxErrorIndex].errorMsg = 'syntax error';
      isExpValid = false;
    }
    const functionErrorIndex = syntaxStr.search(functionSyntaxError);
    if (isExpValid && functionErrorIndex !== -1) {
      // If an error has been found, validation stops.
      expElements[functionErrorIndex].type = 'e';
      expElements[functionErrorIndex].errorMsg = 'incorrect function syntax';
      isExpValid = false;
    }

    if (isServerRequired) {
      let errorPosition;
      const functionNewStack = [];
      let thisFunction;
      let foundArgumentError = false;
      for (let i = 0; i < expElements.length; i += 1) {
        // the ladder search ;)
        if (expElements[i].type === 'f') {
          thisFunction = expElements[i].functionInfo;
          thisFunction.parsedArguments = '';
          functionNewStack.push(expElements[i].functionInfo);
        } else if (
          !foundArgumentError &&
          expElements[i].type === ')' &&
          expElements[i].functionInfo
        ) {
          // this is the end of a function
          thisFunction = expElements[i].functionInfo;
          if (thisFunction !== functionNewStack[functionNewStack.length - 1]) {
            console.warn('This should never happen. (in simple expression)');
          }
          if (
            thisFunction.parsedArguments.replace(
              new RegExp(thisFunction.syntax),
              '',
            ) !== ''
          ) {
            isExpValid = false;
            foundArgumentError = true;
            errorPosition = expElements[i].twinIndex - 1;
            expElements[
              errorPosition
            ].errorMsg = `Incorrect argument syntax for function ${
              thisFunction.name
            }`;
            expElements[errorPosition].type = 'e';
          } else {
            thisFunction = functionNewStack.pop();
            if (functionNewStack.length > 0) {
              functionNewStack[functionNewStack.length - 1].parsedArguments +=
                thisFunction.evaluatesTo;
            }
          }
        } else if (expElements[i].type === 'i') {
          // identifiers
          if (!expElements[i].isLiteral) {
            // identifiers that aren't litterals
            if (functionNewStack.length > 0) {
              functionNewStack[functionNewStack.length - 1].parsedArguments +=
                expElements[i].recordType;
            }
          } else if (functionNewStack.length > 0) {
            // literals
            functionNewStack[functionNewStack.length - 1].parsedArguments +=
              'd';
          }
        }
      }
    }

    const updatedContent = expElements
      .map(element => {
        if (
          element.type === '(' ||
          element.type === ')' ||
          element.type === ',' ||
          element.type === 'o'
        ) {
          return `<span class="blue">${element.label} </span>&nbsp;&nbsp;`;
        }

        if (element.type === 'n') {
          return `<span class="purple-bold">${
            element.label
          } </span>&nbsp;&nbsp;`;
        }

        if (element.type === 'i') {
          if (element.isValidated) {
            if (element.isServerValidatedResult === 'e') {
              isExpValid = false;
              return `<span data-tooltip aria-haspopup="true" class="red has-tip"
            title="Invalid Identifier">${this.addslash(
              element.label,
            )} </span>&nbsp;&nbsp;`;
            }
            return `<span data-tooltip aria-haspopup="true" class="green has-tip"
          title="${element.tooltip}">${this.addslash(
              element.label,
            )} </span>&nbsp;&nbsp;`;
          } if (element.isLiteral) {
            return `<span>${element.label} </span>&nbsp;&nbsp;`;
          }
          return `<span>${element.label.replace(/</g, '&lt;')} </span>&nbsp;&nbsp;`;
        }

        if (element.type === 'f') {
          return `<span class="green">${element.label} </span>&nbsp;&nbsp;`;
        }

        if (element.type === 'e') {
          isExpValid = false;
          return `<span data-tooltip aria-haspopup="true" class="coding-box-red has-tip"
         title="${element.errorMsg}">${this.addslash(
            element.label,
          )} </span>&nbsp;&nbsp;`;
        }

        return ' ';
      })
      .join(' ');

    const html = `${updatedContent}&nbsp;`;
    if (this.state.html !== html) {
      this.setState({ html }, () => {
        if (this.state.autoFormat) {
          this.handleAutoformat(true);
        } else {
          const el = document.getElementById('expression-coding-box');
          const range = document.createRange();
          range.selectNodeContents(el);
          const sel = window.getSelection();
          if (el.lastChild) {
            range.setStart(el.lastChild, 1);
          }
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
          el.focus();
        }
      });
    }

    this.setState({ syntaxExpression: syntaxStr, isExpValid }, () => {
      const expBoxTitle = editProbability ? this.state.expBoxTitle :
        this.manageExpressionTitle();
      this.setState({ expBoxTitle });
      this.props.handleValidation(
        isExpValid,
        expBoxTitle,
        this.state.isVerifiable,
      );
      if (isVerify) {
        this.handleVerify(verifyData);
      }
    });
  };

  handleClearBox = () =>
    this.setState(() => ({
      html: '',
      identifiers: {},
      isExpValid: false,
      expBoxTitle: '',
      objArray: {},
      objSmartSearchArray: {},
      functionExpList: [],
    }));

  handleOperator = operator => {
    const eboxElem = document.getElementById('expression-coding-box');
    eboxElem.focus();
    this.pasteHtmlAtCaret(eboxElem, [operator]);
  };

  handleVerifiedKeywords = keywords => {
    const newIdentifiers = keywords.reduce(
      (acc, { label, tooltip, recordType, isLabelUpdated, isServerValidatedResult }) => ({
        ...acc,
        [label]: {
          isValidated: true,
          tooltip,
          recordType,
          isLabelUpdated,
          isServerValidatedResult,
        },
      }),
      {},
    );
    this.setState(
      { identifiers: { ...this.state.identifiers, ...newIdentifiers } },
      () => this.validateFormatContent(false),
    );
  };

  handleGeographyKeywords = keywords => {
    let expression = '';
    let expBoxTitle = '';
    document.getElementById('expression-coding-box').innerHTML = '';
    if (keywords) {
      let newIdentifiers;
      Object.keys(keywords).forEach(fname => {
        if (keywords[fname] && keywords[fname].length > 0) {
          const arrKeywords = keywords[fname].map(entry => ({
            label: entry.nodeId,
            tooltip: entry.name,
          }));
          newIdentifiers = arrKeywords.reduce(
            (acc, { label, tooltip }) => ({
              ...acc,
              [label]: { isValidated: true, tooltip },
            }),
            {},
          );
          if (keywords[fname].length === 1) {
            if (expression.length > 0) {
              expBoxTitle = 'Custom Geography';
              expression = `${expression} OR ${fname} ( ${
                keywords[fname][0].nodeId
              } )`;
            } else {
              expBoxTitle = keywords[fname][0].name;
              expression = `${fname} ( ${keywords[fname][0].nodeId} )`;
            }
          } else if (keywords[fname].length > 1) {
            const arrselectedNodes = keywords[fname].map(item => item.nodeId);
            if (expression.length > 0) {
              expBoxTitle = 'Custom Geography';
              expression = `${expression} OR ${fname} ( ${arrselectedNodes.join(
                ', ',
              )} )`;
            } else {
              switch (fname) {
                case 'INREGION':
                  expBoxTitle = 'Regions';
                  break;
                case 'INSTATE':
                  expBoxTitle = 'States';
                  break;
                case 'INMARKET':
                  expBoxTitle = 'Markets';
                  break;
                case 'INCOUNTY':
                  expBoxTitle = 'Counties';
                  break;
                case 'INZIP':
                  expBoxTitle = 'Zip Codes';
                  break;
                default:
                  expBoxTitle = '';
              }
              expression = `${fname} ( ${arrselectedNodes.join(', ')} )`;
            }
          }
        }
      });
      this.setState(
        { identifiers: { ...this.state.identifiers, ...newIdentifiers } },
        () => {
          const eboxElem = document.getElementById('expression-coding-box');
          eboxElem.focus();
          this.pasteHtmlAtCaret(eboxElem, [expression]);
          this.setState({ expBoxTitle });
        },
      );
    }
  };

  handleKeywordsDrop = (keywords, objSmartSearchArray) => {
    if (Object.keys(this.state.objSmartSearchArray).length > 0) {
      objSmartSearchArray.expressionFormed = [...this.state.objSmartSearchArray.expressionFormed, ...objSmartSearchArray.expressionFormed];
      objSmartSearchArray.objectsAdded = [...this.state.objSmartSearchArray.objectsAdded, ...objSmartSearchArray.objectsAdded];
      objSmartSearchArray.tags = [...this.state.objSmartSearchArray.tags, ...objSmartSearchArray.tags];
    } else {
      objSmartSearchArray.expressionFormed = [...objSmartSearchArray.expressionFormed];
      objSmartSearchArray.objectsAdded = [...objSmartSearchArray.objectsAdded];
      objSmartSearchArray.tags = [...objSmartSearchArray.tags];
    }

    this.setState({ objSmartSearchArray });
    if (keywords.constructor !== Array) {
      this.handleGeographyKeywords(keywords);
    } else {
      const newIdentifiers = keywords.reduce(
        (acc, { label, tooltip, recordType, isLabelUpdated, keyword }) => ({
          ...acc,
          [label.trim()]: { isValidated: true, tooltip, recordType, isLabelUpdated, keyword },
        }),
        {},
      );
      let isLabelUpdatedFlag = false;
      for (const key in newIdentifiers) {
        if (newIdentifiers[key].isLabelUpdated) {
          isLabelUpdatedFlag = true;
        }
      }
      if (isLabelUpdatedFlag) {
        this.props.toggleLabelUpdate();
      }
      const eboxElem = document.getElementById('expression-coding-box');
      this.setState(
        { identifiers: { ...this.state.identifiers, ...newIdentifiers } },
        () => {
          if (keywords.length === 1) {
            if (keywords[0].recordType === 'L' || keywords[0].recordType === 'G') {
              eboxElem.innerHTML = '';
            }
            eboxElem.focus();
            this.pasteHtmlAtCaret(eboxElem, [keywords[0].label]);
          } else if (keywords.some(key => key.label.indexOf('(') !== -1) &&
          this.props.moduleStudy.st.toLowerCase()
          .includes(FLEX_STUDY_TAG)) {
            this.formExpression(eboxElem, keywords);
          } else {
            this.setState({ dropKeywords: keywords }, () =>
              this.multiKeywordDropModal.show(),
            );
          }
        },
      );
    }
  };

  formExpression = (eboxElem, keywords) => {
    const expBoxElem = eboxElem;
    expBoxElem.innerHTML = '';
    let expBoxTitle = '';
    let strRegionsData = '';
    let strStatesData = '';
    let strMarketsData = '';
    let strCountyData = '';
    let strZipData = '';
    let finalExpArr = [];
    keywords.forEach(keyword => {
      if (keyword.label.indexOf('(') === -1) {
        finalExpArr.push(keyword.label);
      } else {
        const fname = keyword.label.slice(0, keyword.label.indexOf('(')).trim();
        switch (fname) {
          case 'INREGION':
            if (strRegionsData === '') {
              strRegionsData = keyword.label;
            } else {
              strRegionsData = strRegionsData.slice(
                0,
                strRegionsData.length - 2,
              );
              strRegionsData = `${strRegionsData}, ${keyword.label.slice(
                keyword.label.indexOf('(') + 2,
                keyword.label.indexOf(')') - 1,
              )} )`;
            }
            expBoxTitle = 'Regions';
            break;
          case 'INSTATE':
            if (strStatesData === '') {
              strStatesData = keyword.label;
            } else {
              strStatesData = strStatesData.slice(0, strStatesData.length - 2);
              strStatesData = `${strStatesData}, ${keyword.label.slice(
                keyword.label.indexOf('(') + 2,
                keyword.label.indexOf(')') - 1,
              )} )`;
            }
            expBoxTitle = 'States';
            break;
          case 'INMARKET':
            if (strMarketsData === '') {
              strMarketsData = keyword.label;
            } else {
              strMarketsData = strMarketsData.slice(
                0,
                strMarketsData.length - 2,
              );
              strMarketsData = `${strMarketsData}, ${keyword.label.slice(
                keyword.label.indexOf('(') + 2,
                keyword.label.indexOf(')') - 1,
              )} )`;
            }
            expBoxTitle = 'Markets';
            break;
          case 'INCOUNTY':
            if (strCountyData === '') {
              strCountyData = keyword.label;
            } else {
              strCountyData = strCountyData.slice(0, strCountyData.length - 2);
              strCountyData = `${strCountyData}, ${keyword.label.slice(
                keyword.label.indexOf('(') + 2,
                keyword.label.indexOf(')') - 1,
              )} )`;
            }
            expBoxTitle = 'Counties';
            break;
          case 'INZIP':
            if (strZipData === '') {
              strZipData = keyword.label;
            } else {
              strZipData = strZipData.slice(0, strZipData.length - 2);
              strZipData = `${strZipData}, ${keyword.label.slice(
                keyword.label.indexOf('(') + 2,
                keyword.label.indexOf(')') - 1,
              )} )`;
            }
            expBoxTitle = 'Zip Codes';
            break;
          default:
            expBoxTitle = '';
        }
      }
    });
    if (strRegionsData !== '') {
      finalExpArr = [...finalExpArr, strRegionsData];
    }
    if (strStatesData !== '') {
      finalExpArr = [...finalExpArr, strStatesData];
    }
    if (strMarketsData !== '') {
      finalExpArr = [...finalExpArr, strMarketsData];
    }
    if (strCountyData !== '') {
      finalExpArr = [...finalExpArr, strCountyData];
    }
    if (strZipData !== '') {
      finalExpArr = [...finalExpArr, strZipData];
    }
    const expression = finalExpArr.join(' OR ');
    this.pasteHtmlAtCaret(expBoxElem, [expression]);
    this.setState({ expBoxTitle });
  };

  handleCrosstabEditToDictionary = (idKey, dataSmartSearch, keyword) => {
    if (keyword) {
      const eboxElem = document.getElementById('expression-coding-box');
      eboxElem.focus();
      this.setState({ html: '' }, () => {
        this.pasteHtmlAtCaret(eboxElem, keyword);
      });
    }
  };

  handleProbabilitiesEditClick = (rowData, colData) => {
    let rowKeyword = '';
    let colKeyword = '';
    let rowTitle = '';
    let colTitle = '';
    if (rowData && colData) {
      if (rowData.keyword.startsWith('(')) {
        rowKeyword = rowData.keyword;
        rowTitle = rowData.originalTitle;
      } else {
        rowKeyword = `( ${rowData.keyword} )`;
        rowTitle = `( ${rowData.originalTitle} )`;
      }
      if (colData.keyword.startsWith('(')) {
        colKeyword = colData.keyword;
        colTitle = colData.originalTitle;
      } else {
        colKeyword = `( ${colData.keyword} )`;
        colTitle = `( ${colData.originalTitle} )`;
      }
      const keyword = `${rowKeyword} AND ${colKeyword}`;
      const expBoxTitle = `${rowTitle} AND ${colTitle}`;
      const dataSmartSearch = {
        expressionFormed: [...rowData.dataSmartSearch.expressionFormed,
        ...colData.dataSmartSearch.expressionFormed],
        objectsAdded: [...rowData.dataSmartSearch.objectsAdded,
          ...colData.dataSmartSearch.objectsAdded],
        tags: [...rowData.dataSmartSearch.tags,
          ...colData.dataSmartSearch.tags],
      };
      if (keyword) {
        const eboxElem = document.getElementById('expression-coding-box');
        eboxElem.focus();
        this.setState({
          html: '',
          expBoxTitle,
          objSmartSearchArray: dataSmartSearch,
          objArray: [...dataSmartSearch.objectsAdded] }, () => {
          this.pasteHtmlAtCaret(eboxElem, keyword, true);
        });
      }
    }
  };

  handleSelectedKeywords = (
    selectedKeywordNodes,
    titlePrefix,
    selectedOperator,
  ) => {
    const breadcrumbList = JSON.parse(window.localStorage.getItem('breadCrumb'));
    const breadCrumbValue = breadcrumbList ? breadcrumbList.reverse().join(' > ') : undefined;

    const objSmartSearchArray = {};

    const newIdentifiers = selectedKeywordNodes.reduce(
      (acc, { keyword, shortText, recordType, isLabelUpdated }) => ({
        ...acc,
        [keyword.toUpperCase()]: {
          isValidated: true,
          tooltip: `${titlePrefix}${shortText}`,
          recordType,
          isLabelUpdated,
        },
      }),
      {},
    );
    const objectsAdded = [];
    const expressionFormed = [];
    const tags = [];
    const selectedNodes = cloneDeep(selectedKeywordNodes);
    selectedNodes.forEach(node => {
      node.breadcrumbs = breadCrumbValue;
      objectsAdded.push(node);
      expressionFormed.push(node.keyword);
      tags.push(node.shortText);
    });
    objSmartSearchArray.objectsAdded = objectsAdded;
    objSmartSearchArray.expressionFormed = expressionFormed;
    objSmartSearchArray.tags = tags;

    if (Object.keys(this.state.objSmartSearchArray).length > 0) {
      objSmartSearchArray.expressionFormed = [...this.state.objSmartSearchArray.expressionFormed, ...objSmartSearchArray.expressionFormed];
      objSmartSearchArray.objectsAdded = [...this.state.objSmartSearchArray.objectsAdded, ...objSmartSearchArray.objectsAdded];
      objSmartSearchArray.tags = [...this.state.objSmartSearchArray.tags, ...objSmartSearchArray.tags];
    } else {
      objSmartSearchArray.expressionFormed = [...objSmartSearchArray.expressionFormed];
      objSmartSearchArray.objectsAdded = [...objSmartSearchArray.objectsAdded];
      objSmartSearchArray.tags = [...objSmartSearchArray.tags];
    }

    this.setState({ objSmartSearchArray });

    this.setState(
      { identifiers: { ...this.state.identifiers, ...newIdentifiers } },
      () => {
        if (selectedKeywordNodes.length === 1) {
          const eboxElem = document.getElementById('expression-coding-box');
          eboxElem.focus();
          this.pasteHtmlAtCaret(eboxElem, [selectedKeywordNodes[0].keyword]);
        } else {
          // TODO: Show context popup
          const inputs = selectedKeywordNodes.slice(1).reduce(
            (acc, val) => {
              acc.push(selectedOperator);
              acc.push(val.keyword);
              return acc;
            },
            ['(', selectedKeywordNodes[0].keyword],
          );
          inputs.push(')');
          const eboxElem = document.getElementById('expression-coding-box');
          eboxElem.focus();
          this.pasteHtmlAtCaret(eboxElem, inputs.join(' '));
        }
        this.setState({ expBoxTitle: selectedKeywordNodes[0].shortText });
      },
    );
  };

  handleKeyUp = () => {
    const contentExtractor = document.createElement('div');
    contentExtractor.innerHTML = this.state.html.replace(/&nbsp;/g, '');
    const extractedContents =
      contentExtractor.textContent || contentExtractor.innerText;
    const isVerifiable = extractedContents.length;
    if (window.getSelection) {
      const sel = window.getSelection();
      this.setState({ anchorOffset: sel.anchorOffset });
    }
    if (this.state.isExpValid) {
      this.setState({ isExpValid: false, isVerifiable }, () => {
        const expBoxTitle = this.manageExpressionTitle();
        this.setState({ expBoxTitle });
        this.props.handleValidation(
          this.state.isExpValid,
          expBoxTitle,
          this.state.isVerifiable,
        );
      });
    } else {
      this.setState({ isVerifiable }, () => {
        const expBoxTitle = this.manageExpressionTitle();
        this.setState({ expBoxTitle });
        this.props.handleValidation(
          this.state.isExpValid,
          expBoxTitle,
          this.state.isVerifiable,
        );
      });
    }
  };

  handleOperatorObjects = operator => {
    const operatorObj = {
      node: '',
      recType: ' ',
      keyword: `${operator}`,
      shortText: `${operator}`,
      shortTextCatgView: `${operator}`,
      objectType: 'operator',
    };
    return operatorObj;
  }

  manageExpressionTitle = () => {
    let expBoxTitle = '';
    let codingBoxText = $('#expression-coding-box').children().text().trim();
    codingBoxText = codingBoxText.replace(/ +(?= )/g, '');
    const identifiers = this.state.identifiers;

    const functionExpList = this.state.functionExpList;

    const isGeoData = GEOGRAPHY_BASES.some(item => codingBoxText.includes(item));
    if (codingBoxText.length > 0) {
      const objSmartSearchArray = this.state.objSmartSearchArray;
      const objectsAdded = objSmartSearchArray.objectsAdded || [];
      const expressionFormed = objSmartSearchArray.expressionFormed || [];
      const definitionExpressionArr = [];
      let newCodingBoxTextArr = codingBoxText;
      let index = 0;
      let functionIndex = 0;

      const finalObjectsAdded = [];

      objectsAdded.forEach(objects => {
        let expText = objects.expression ? objects.expression.trim() : '';
        expText = expText.replace(/ +(?= )/g, '');
        if (expText && codingBoxText.indexOf(expText) !== -1
            && definitionExpressionArr.indexOf(expText) === -1) {
          definitionExpressionArr.push(expText);
          newCodingBoxTextArr = newCodingBoxTextArr.replace(expText, ` @#@${index}@#@ `);
          index += 1;
        } else if(expText !== undefined) {
          expText = expText.toUpperCase();
          expText = expText.replaceAll('(', ' ( ');
          expText = expText.replaceAll(')', ' ) ');
          expText = expText.replace(/  +/g, ' ');
          expText = expText.trim();
          if (expText && codingBoxText.indexOf(expText) !== -1
            && definitionExpressionArr.indexOf(expText) === -1) {
            definitionExpressionArr.push(expText);
            newCodingBoxTextArr = newCodingBoxTextArr.replace(expText, ` @#@${index}@#@ `);
            index += 1;
          }
        }
      });
      functionExpList.forEach(func => {
        func = func.replace(/ +(?= )/g, '');
        if (func && codingBoxText.indexOf(func) !== -1) {
          newCodingBoxTextArr = newCodingBoxTextArr.replace(func, ` @@@${functionIndex}@@@ `);
          functionIndex += 1;
        }
      });
      const newObjectsAdded = [];
      newCodingBoxTextArr = newCodingBoxTextArr.split(' ');
      let definitionLefttext = '';
      newCodingBoxTextArr.forEach((nodes, nodeIndex) => {
        if (nodes.length > 0) {
          nodes = nodes.trim();
          if (definitionLefttext.length === 0 && ValidOperators.indexOf(nodes) !== -1) {
            expBoxTitle = `${expBoxTitle} ${nodes}`;
            newObjectsAdded.push(this.handleOperatorObjects(nodes));
            finalObjectsAdded.splice(nodeIndex, 0, this.handleOperatorObjects(nodes));
            
          } else if (definitionLefttext.length === 0 && nodes.indexOf('@@@') === 0) {
            const funcIndex = nodes.replace(/@@@/g, '');
            const functionExp = functionExpList[funcIndex];
            const functionExpArr = functionExp.split(' ');
            if (identifiers[functionExp]) {
              expBoxTitle = `${expBoxTitle} ${identifiers[functionExp].tooltip}`;
            }
            let title = '';
            functionExpArr.forEach(funcElems => {
              const index = findIndex (objectsAdded, { keyword: funcElems });
              if(index > -1) {
                newObjectsAdded.push(objectsAdded[index]);
                title += ` ${ objectsAdded[index].shortText} `;
              } else {
                title += ` ${funcElems} `;
              }
            });
            if (expBoxTitle.trim() === '' && title !== '') {
              expBoxTitle = title.replace(',  ,', ',').replace(',    ) ', ' )');
            }
          } else if (definitionLefttext.length === 0 && nodes.indexOf('@#@') === 0) {
            const defIndex = nodes.replace(/@#@/g, '');
            const definitionExp = definitionExpressionArr[defIndex];
            objectsAdded.forEach(items => {
              let expression = items.expression ? items.expression.trim().replace(/ +(?= )/g, '') : items.expression;
              if (expression && expression === definitionExp) {
                const title = items.title ? items.title.toUpperCase() : '';
                expBoxTitle = `${expBoxTitle} ${title}`;
                newObjectsAdded.push(items);
                finalObjectsAdded.splice(nodeIndex, 0, items);
                
              } else if(expression !== undefined) {
                expression = expression.toUpperCase();
                expression = expression.replaceAll('(', ' ( ');
                expression = expression.replaceAll(')', ' ) ');
                expression = expression.replace(/  +/g, ' ');
                expression = expression.trim();
                if (expression && expression === definitionExp) {
                  const title = items.title ? items.title.toUpperCase() : '';
                  expBoxTitle = `${expBoxTitle} ${title}`;
                  newObjectsAdded.push(items);
                  finalObjectsAdded.splice(nodeIndex, 0, items);
                  
                }
              }
            });
          } else if (definitionLefttext.length === 0 && expressionFormed && expressionFormed.indexOf(nodes) !== -1) {
            objectsAdded.forEach(items => { // Dictionary
              if (items.keyword && items.keyword === nodes) {
                expBoxTitle = `${expBoxTitle} ${items.shortText}`;
                newObjectsAdded.push(items);
                finalObjectsAdded.splice(nodeIndex, 0, items);
                
              } else if (items.fname && items.fname === nodes) { // Geographies
                expBoxTitle = `${expBoxTitle} ${items.name}`;
                newObjectsAdded.push(items);
                finalObjectsAdded.splice(nodeIndex, 0, items);
                
              }
            });
          } else {
            let definitionFlag = false;
            const definitionArr = [];
            objectsAdded.forEach(objects => {
              let expText = objects.expression ? objects.expression.trim() : '';
              expText = expText.replace(/ +(?= )/g, '');
              if (expText.length > 0 && definitionArr.indexOf(expText) === -1) {
                definitionArr.push(expText);
              }
            });
            let operatorFlag =  false;
            definitionArr.forEach(definition => {
              if(ValidOperators.indexOf(nodes) !== -1) {
                operatorFlag = true;
                
              } else if (definition.includes(nodes)) {
                definitionFlag = true;
                if (definitionLefttext.length === 0) {
                  objectsAdded.forEach(items => {
                    const itemExp = items.expression ? items.expression.replace(/  +/g, ' ') : items.expression;
                    if (itemExp && itemExp.includes(definition)) {
                      const title = items.title ? items.title.toUpperCase() : '';
                      expBoxTitle = `${expBoxTitle} ${title}`;
                      newObjectsAdded.push(items);
                      finalObjectsAdded.splice(nodeIndex, 0, items);
                      
                    }
                  });
                }
                definitionLefttext = `${definitionLefttext} ${nodes}`;
                
              }
            });
            if(operatorFlag) {
              expBoxTitle = `${expBoxTitle} ${nodes}`;
              newObjectsAdded.push(this.handleOperatorObjects(nodes));
              finalObjectsAdded.slice(nodeIndex, 0, this.handleOperatorObjects(nodes));
            }
            if (!definitionFlag) {
              definitionLefttext = '';
              const expWithParan = expressionFormed.filter(exps => exps && exps.includes(nodes));
              if (ValidOperators.indexOf(nodes) !== -1) {
                if(!operatorFlag) {
                  expBoxTitle = `${expBoxTitle} ${nodes}`;
                  newObjectsAdded.push(this.handleOperatorObjects(nodes));
                }

                finalObjectsAdded.splice(nodeIndex, 0, this.handleOperatorObjects(nodes));
                
              } else if (nodes.indexOf('@#@') === 0) {
                const defIndex = nodes.replace(/@#@/g, '');
                const definitionExp = definitionExpressionArr[defIndex];
                objectsAdded.forEach(items => {
                  let expression = items.expression ? items.expression.trim().replace(/ +(?= )/g, '') : items.expression;
                  if (expression && expression.includes(definitionExp)) {
                    const title = items.title ? items.title.toUpperCase() : '';
                    expBoxTitle = `${expBoxTitle} ${title}`;
                    newObjectsAdded.push(items);
                    finalObjectsAdded.splice(nodeIndex, 0, items);
                    
                  } else if(expression !== undefined) {
                    expression = expression.toUpperCase();
                    expression = expression.replaceAll('(', ' ( ');
                    expression = expression.replaceAll(')', ' ) ');
                    expression = expression.replace(/  +/g, ' ');
                    expression = expression.trim();
                    if (expression && expression.includes(definitionExp)) {
                      const title = items.title ? items.title.toUpperCase() : '';
                      expBoxTitle = `${expBoxTitle} ${title}`;
                      newObjectsAdded.push(items);
                      finalObjectsAdded.splice(nodeIndex, 0, items);
                      
                    }
                  }
                });

              } else if (expressionFormed && expressionFormed.indexOf(nodes) !== -1) {
                objectsAdded.forEach(items => {
                  if (items.keyword && items.keyword === nodes) {
                    expBoxTitle = `${expBoxTitle} ${items.shortText}`;
                    newObjectsAdded.push(items);
                    finalObjectsAdded.splice(nodeIndex, 0, items);
                    
                  } else if (items.fname && items.fname === nodes) {
                    expBoxTitle = `${expBoxTitle} ${items.name}`;
                    newObjectsAdded.push(items);
                    finalObjectsAdded.splice(nodeIndex, 0, items);
                    
                  }
                });
              } else if (expWithParan && expWithParan.length > 0) {
                objectsAdded.forEach(items => {
                  if (items.keyword && items.keyword === nodes) {
                    expBoxTitle = `${expBoxTitle} ${items.shortText}`;
                    newObjectsAdded.push(items);
                    finalObjectsAdded.splice(nodeIndex, 0, items);
                    
                  } else if (items.fname && items.fname === nodes) {
                    expBoxTitle = `${expBoxTitle} ${items.name}`;
                    newObjectsAdded.push(items);
                    finalObjectsAdded.splice(nodeIndex, 0, items);
                    
                  }
                });
              } else if (identifiers[nodes]) {
                expBoxTitle = `${expBoxTitle} ${identifiers[nodes].tooltip}`;
                const newItem = {
                  breadcrumbs: identifiers[nodes].tooltip,
                  keyword: identifiers[nodes].keyword,
                  midpoint: null,
                  modeled: false,
                  objectType: 'operand',
                  recordType: identifiers[nodes].recordType,
                  rectype: identifiers[nodes].recordType,
                  shortText: identifiers[nodes].tooltip,
                  shortTextCatgView: identifiers[nodes].tooltip,
                };
                newObjectsAdded.push(newItem);
              } else if (isGeoData) {
                const keyList = Object.keys(identifiers);
                let newItem;
                if (keyList.length > 1) {
                  const keyVal = keyList.filter(item => item.includes(nodes))[0];
                  if (keyVal) {
                    newItem = {
                      breadcrumbs: identifiers[keyVal].tooltip,
                      keyword: identifiers[keyVal].keyword,
                      midpoint: null,
                      modeled: false,
                      objectType: 'operand',
                      recordType: identifiers[keyVal].recordType,
                      rectype: identifiers[keyVal].recordType,
                      shortText: identifiers[keyVal].tooltip,
                      shortTextCatgView: identifiers[keyVal].tooltip,
                    };
                  } else {
                    newItem = {
                      breadcrumbs: identifiers.tooltip,
                      keyword: identifiers.keyword,
                      midpoint: null,
                      modeled: false,
                      objectType: 'operand',
                      recordType: identifiers.recordType,
                      rectype: identifiers.recordType,
                      shortText: identifiers.tooltip,
                      shortTextCatgView: identifiers.tooltip,
                    };
                  }
                } else if (keyList.length === 1) {
                  newItem = {
                    breadcrumbs: identifiers.tooltip,
                    keyword: identifiers.keyword,
                    midpoint: null,
                    modeled: false,
                    objectType: 'operand',
                    recordType: identifiers.recordType,
                    rectype: identifiers.recordType,
                    shortText: identifiers.tooltip,
                    shortTextCatgView: identifiers.tooltip,
                  };
                }
                newObjectsAdded.push(newItem);
                const arrList = Object.keys(identifiers);
                let firstText = '';
                let secondText = ' ( ';
                arrList.forEach(item => {
                  const shortText = identifiers[item].tooltip;
                  if (firstText === '') {
                    firstText = shortText;
                  } else {
                    firstText = `${firstText} ${shortText}`;
                  }
                  secondText = `${secondText} ${shortText}`;
                });
                expBoxTitle = firstText + secondText;
              } else {
                expBoxTitle = `${expBoxTitle} ${nodes}`;
              }
            }
          }
        }
      });

      // console.log('finalObjectsAdded => ', finalObjectsAdded, ', newObjectsAdded => ',
      // newObjectsAdded, ', expBoxTitle => ', expBoxTitle);
      this.setState({ objArray: newObjectsAdded, finalObjectsAdded });
      return expBoxTitle;
    } 
      this.setState({ objSmartSearchArray: {}, objArray: {}, finalObjectsAdded: [] });
      return expBoxTitle;
    
    return this.state.expBoxTitle;
  }

  handleChange = e => {
    this.setState({ html: e.target.value });
  }

  handleClickCE = () => {
    if (window.getSelection) {
      const sel = window.getSelection();
      this.setState({ anchorOffset: sel.anchorOffset });
    }
  };

  moveAppendToCodingBox = (isMove, expressionKeyword, functionTitle, smartSearchArray) => {
    this.handleKeywordsDrop([{
      label: expressionKeyword,
      tooltip: functionTitle,
      isLabelUpdated: true,
      recordType: smartSearchArray.objectsAdded[0].rectype }],
      smartSearchArray);

    const functionExpList = this.state.functionExpList;
    if (functionExpList.indexOf(expressionKeyword) === -1) {
      functionExpList.push(expressionKeyword);
      this.setState({ functionExpList });
    }

    if (isMove) {
      this.setState(
        { html: expressionKeyword, expBoxTitle: functionTitle },
        () => this.validateFormatContent(false),
      );
    }
  };

  render() {
    const { connectDropTarget, isOver, canDrop } = this.props;

    return connectDropTarget(
      <div
        className={
          canDrop && isOver
            ? 'expression-coding-box droppable'
            : 'expression-coding-box'
        }
      >
        <ContentEditable
          id="expression-coding-box"
          html={this.state.html}
          onKeyUp={this.handleKeyUp}
          onChange={this.handleChange}
          onClick={this.handleClickCE}
          className={
            this.state.isExpValid
              ? 'expression-coding-box expression-valid'
              : this.state.html === ''
              ? 'expression-coding-box'
              : 'expression-coding-box expression-invalid'
          }
          spellCheck="false"
        />
        <Modal
          title="Choose options"
          ref={node => (this.multiKeywordDropModal = node)}
          dialogStyles={modalStyle}
        >
          <div className="large-12 columns">
            <div className="column row">
              <select
                className="ds-drag-option"
                onChange={e => this.handleSelectBoxOperator(e.target.value)}
              >
                <option value="0">Select option</option>
                <option value="AND">AND</option>
                <option value="OR">OR</option>
                <option value="CANCEL">CANCEL</option>
              </select>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

ExpressionCodingBox.propTypes = {
  connectDropTarget: PropTypes.func.isRequired,
  isOver: PropTypes.bool.isRequired,
  canDrop: PropTypes.bool.isRequired,
  handleValidation: PropTypes.func,
  boxTitle: PropTypes.string,
  moduleStudy: PropTypes.shape({
    st: PropTypes.string,
  }),
  toggleLabelUpdate: PropTypes.func,
};

export default dropTarget(
  [
    DnDItemTypes.DnD_ITEM_TYPE_2,
    DnDItemTypes.DnD_ITEM_TYPE_3,
    DnDItemTypes.DnD_ITEM_TYPE_4,
    DnDItemTypes.DnD_ITEM_TYPE_7,
  ],
  keyWordTarget,
  collect,
)(ExpressionCodingBox);
